import { computed } from 'vue';
import emojisGroup from 'shared/components/emoji/emojisGroup.json';

/**
 * @typedef {Object} EmojiDebugInfo
 * @property {string} input - Original input value
 * @property {string} output - Processed output value
 * @property {boolean} isInputEmoji - Whether input is a valid emoji
 * @property {boolean} isOutputEmoji - Whether output is a valid emoji
 * @property {boolean} foundInGroup - Whether emoji was found in predefined group
 * @property {boolean} isSlug - Whether input appears to be an emoji slug
 */

/**
 * Function to detect if a string is a valid emoji
 *
 * @example
 * ```js
 * isEmoji('🚀')           // true
 * isEmoji('🇮🇩')          // true
 * isEmoji('flag_indonesia') // false
 * isEmoji('hello')        // false
 * ```
 *
 * @param {string} str - String to test
 * @returns {boolean} True if string contains valid emoji characters
 */
export function isEmoji(str) {
  if (!str) return false;

  // More comprehensive emoji detection including newer Unicode ranges
  // This covers most emoji ranges including skin tones, flags, and newer emojis
  const emojiRegex =
    /[\u{1f300}-\u{1f5ff}\u{1f900}-\u{1f9ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{2600}-\u{26ff}\u{2700}-\u{27bf}\u{1f1e6}-\u{1f1ff}\u{1f191}-\u{1f251}\u{1f004}\u{1f0cf}\u{1f170}-\u{1f171}\u{1f17e}-\u{1f17f}\u{1f18e}\u{3030}\u{2b50}\u{2b55}\u{2934}-\u{2935}\u{2b05}-\u{2b07}\u{2b1b}-\u{2b1c}\u{3297}\u{3299}\u{303d}\u{00a9}\u{00ae}\u{2122}\u{23f3}\u{24c2}\u{23e9}-\u{23ef}\u{25b6}\u{23f8}-\u{23fa}]/gu;

  return emojiRegex.test(str);
}

/**
 * Function to convert emoji slug to actual emoji or validate existing emoji
 *
 * @example
 * ```js
 * getEmojiFromSlug('🚀')            // '🚀' (already emoji)
 * getEmojiFromSlug('flag_indonesia') // '🇮🇩' (converts slug)
 * getEmojiFromSlug('rocket')        // '🚀' (converts slug)
 * getEmojiFromSlug('unknown_slug')  // 'unknown_slug' (not found)
 * ```
 *
 * @param {string} slug - Emoji slug or actual emoji character
 * @returns {string} Actual emoji character or original input if not found
 */
export function getEmojiFromSlug(slug) {
  if (!slug) return '';

  // If it's already a valid emoji, return as is
  if (isEmoji(slug)) return slug;

  // If it looks like a slug (letters, numbers, underscores only), try to find it in the group
  if (/^[a-zA-Z0-9_]+$/.test(slug)) {
    const foundEmoji = emojisGroup
      .flatMap(group => group.emojis)
      .find(emoji => emoji.slug === slug);

    if (foundEmoji) return foundEmoji.emoji;
  }

  // For any other case (could be a newer emoji not in our list), return as is
  // This handles cases where valid emojis aren't in our predefined group
  return slug;
}

/**
 * Composable for emoji handling and processing
 *
 * @example
 * ```js
 * import { ref } from 'vue';
 * import { useEmoji } from 'dashboard/composables';
 *
 * // With reactive ref
 * const emojiRef = ref('flag_indonesia');
 * const { displayEmoji, debugInfo } = useEmoji(emojiRef);
 * console.log(displayEmoji.value); // '🇮🇩'
 *
 * // With getter function (useful for props)
 * const { displayEmoji } = useEmoji(() => props.emojiIcon);
 *
 * // With plain string
 * const { displayEmoji } = useEmoji('🚀');
 * console.log(displayEmoji.value); // '🚀'
 * ```
 *
 * @param {import('vue').Ref<string>|string|Function} emojiInput - Reactive emoji input, string, or getter function
 * @returns {Object} Emoji utilities and computed values
 * @returns {import('vue').ComputedRef<string>} returns.displayEmoji - Computed emoji display value
 * @returns {import('vue').ComputedRef<EmojiDebugInfo|null>} returns.debugInfo - Debug information (development only)
 * @returns {Function} returns.isEmoji - Direct access to isEmoji function
 * @returns {Function} returns.getEmojiFromSlug - Direct access to getEmojiFromSlug function
 */
export function useEmoji(emojiInput) {
  const displayEmoji = computed(() => {
    let input;

    if (typeof emojiInput === 'function') {
      input = emojiInput();
    } else if (typeof emojiInput === 'string') {
      input = emojiInput;
    } else {
      input = emojiInput.value;
    }

    return getEmojiFromSlug(input);
  });

  /**
   * Debug information about emoji processing (only in development)
   * @type {import('vue').ComputedRef<EmojiDebugInfo|null>}
   */
  const debugInfo = computed(() => {
    if (process.env.NODE_ENV !== 'development') return null;

    let input;

    if (typeof emojiInput === 'function') {
      input = emojiInput();
    } else if (typeof emojiInput === 'string') {
      input = emojiInput;
    } else {
      input = emojiInput.value;
    }

    const result = getEmojiFromSlug(input);

    return {
      input,
      output: result,
      isInputEmoji: isEmoji(input),
      isOutputEmoji: isEmoji(result),
      foundInGroup: input !== result && isEmoji(result),
      isSlug: /^[a-zA-Z0-9_]+$/.test(input),
    };
  });

  return {
    displayEmoji,
    debugInfo,
    isEmoji,
    getEmojiFromSlug,
  };
}
